import React, { useState, useEffect, useCallback } from 'react';
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Plus, X } from 'tabler-icons-react';
import { useCalidad } from './CalidadContext';
import CalidadSerieField from './CalidadSerieField';
import CalidadLineaDefecto from './CalidadLineaDefecto';

let uidSeq = 0;
const nuevaLinea = () => ({
    uid: `ln${(uidSeq += 1)}`,
    parte: '',
    defecto: '',
    causaRaiz: '',
    notas: '',
});

const claveLinea = (l) => `${l.parte}|${l.defecto}`;

// Un solo modal para alta (bulk, N líneas) y edición (1 folio, vía PUT). La
// diferencia entre ambos modos son unos pocos booleanos y la rama del submit
// — duplicarlo habría duplicado también la cascada, la validación y el CSS.
export default function CalidadFormModal() {
    const {
        formOpen,
        editTarget,
        guardando,
        errorGuardado,
        resultadoParcial,
        cerrarForm,
        guardarReporte,
        actualizarRegistro,
        catalogoPartes,
    } = useCalidad();

    const esEdicion = Boolean(editTarget);

    const [loteoSerie, setLoteoSerie] = useState('');
    const [lineas, setLineas] = useState([nuevaLinea()]);
    const [errores, setErrores] = useState({});
    const [errorCabecera, setErrorCabecera] = useState(null);

    useEffect(() => {
        if (!formOpen) return;
        if (esEdicion) {
            setLoteoSerie(editTarget.loteoSerie || '');
            setLineas([
                {
                    uid: `ed${editTarget.folio}`,
                    parte: editTarget.parte ?? '',
                    defecto: editTarget.defecto ?? '',
                    causaRaiz: editTarget.causaRaiz ?? '',
                    notas: editTarget.notas || '',
                },
            ]);
        } else {
            setLoteoSerie('');
            setLineas([nuevaLinea()]);
        }
        setErrores({});
        setErrorCabecera(null);
    }, [formOpen, editTarget, esEdicion]);

    // Fallo parcial del registro múltiple (solo puede darse en modo mock, ver
    // calidadApi.crearReporte): se conservan únicamente las líneas que no
    // llegaron a crearse, para que el operario reintente solo lo que falta.
    useEffect(() => {
        if (!resultadoParcial) return;
        const indicesFallidos = new Set(resultadoParcial.fallidas.map((f) => f.indice));
        setLineas((prev) => prev.filter((_, i) => indicesFallidos.has(i)));
    }, [resultadoParcial]);

    const agregarLinea = () => setLineas((prev) => [...prev, nuevaLinea()]);

    const eliminarLinea = (uid) =>
        setLineas((prev) => (prev.length > 1 ? prev.filter((l) => l.uid !== uid) : prev));

    const cambiarCampo = (uid, campo, valor) =>
        setLineas((prev) => prev.map((l) => (l.uid === uid ? { ...l, [campo]: valor } : l)));

    const validar = useCallback(() => {
        const erroresLineas = {};
        const conteo = new Map();
        let ok = true;

        lineas.forEach((l) => {
            const e = {};
            if (!l.defecto && l.defecto !== 0) {
                e.defecto = 'Selecciona un tipo de defecto';
                ok = false;
            }
            if (catalogoPartes.length > 0 && !l.parte && l.parte !== 0) {
                e.parte = 'Selecciona una parte';
                ok = false;
            }
            conteo.set(claveLinea(l), (conteo.get(claveLinea(l)) || 0) + 1);
            if (Object.keys(e).length > 0) erroresLineas[l.uid] = e;
        });

        // Duplicado = aviso no bloqueante: registrar el mismo defecto dos
        // veces en la misma parte de una unidad es legítimo.
        lineas.forEach((l) => {
            if (l.defecto !== '' && conteo.get(claveLinea(l)) > 1) {
                erroresLineas[l.uid] = { ...erroresLineas[l.uid], duplicado: true };
            }
        });

        const errCabecera = loteoSerie.length > 100 ? 'Máximo 100 caracteres' : null;
        if (errCabecera) ok = false;

        return { ok, erroresLineas, errCabecera };
    }, [lineas, catalogoPartes, loteoSerie]);

    const handleSubmit = async () => {
        const { ok, erroresLineas, errCabecera } = validar();
        setErrores(erroresLineas);
        setErrorCabecera(errCabecera);
        if (!ok) {
            // Con la lista de líneas acotada a un alto máximo, un error puede
            // quedar fuera de vista y el botón parece "no hacer nada".
            const primerUid = lineas.find((l) => {
                const e = erroresLineas[l.uid];
                return e && (e.defecto || e.parte);
            })?.uid;
            if (primerUid) {
                document
                    .getElementById(`calidad-linea-${primerUid}`)
                    ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
            return;
        }

        if (esEdicion) {
            const [linea] = lineas;
            await actualizarRegistro(editTarget.folio, linea, loteoSerie);
        } else {
            await guardarReporte({ loteoSerie, lineas });
        }
    };

    const titulo = esEdicion ? `Editar registro #${editTarget?.folio}` : 'Registrar reporte de calidad';

    return (
        <Modal open={formOpen} onClose={cerrarForm}>
            <div className="usuarios-modal usuarios-modal--wide calidad-form-modal">
                <div className="usuarios-modal-header">
                    <h2 className="usuarios-modal-title">{titulo}</h2>
                    <IconButton size="small" onClick={cerrarForm}>
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </div>

                <div className="usuarios-modal-body">
                    <div className="calidad-form-cabecera">
                        <CalidadSerieField
                            value={loteoSerie}
                            onChange={setLoteoSerie}
                            error={!!errorCabecera}
                            helperText={errorCabecera}
                        />
                    </div>

                    <div className="calidad-lineas-scroll">
                        {lineas.map((linea, i) => (
                            <CalidadLineaDefecto
                                key={linea.uid}
                                numero={i + 1}
                                linea={linea}
                                errores={errores[linea.uid]}
                                onCampo={(campo, valor) => cambiarCampo(linea.uid, campo, valor)}
                                onEliminar={() => eliminarLinea(linea.uid)}
                                permitirEliminar={!esEdicion && lineas.length > 1}
                            />
                        ))}
                    </div>

                    {!esEdicion && (
                        <Button
                            variant="outlined"
                            className="calidad-add-linea"
                            startIcon={<Plus size={16} />}
                            onClick={agregarLinea}
                        >
                            Agregar defecto
                        </Button>
                    )}

                    {errorGuardado && (
                        <div className="calidad-alert-inline calidad-alert-inline--error">{errorGuardado}</div>
                    )}
                </div>

                <div className="usuarios-modal-footer">
                    <Button
                        variant="outlined"
                        className="usuarios-modal-cancel"
                        onClick={cerrarForm}
                        disabled={guardando}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        className="usuarios-modal-submit"
                        onClick={handleSubmit}
                        disabled={guardando}
                        startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : null}
                    >
                        {guardando
                            ? 'Guardando...'
                            : esEdicion
                            ? 'Guardar cambios'
                            : `Registrar ${lineas.length > 1 ? `${lineas.length} defectos` : 'defecto'}`}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
